import React, { createContext, useCallback, useRef, useState, useContext, useEffect } from "react";
import { SettingsContext } from "contexts/settings";
import _ from "lodash";
import moment from "moment";

export const AlertContext = createContext({
    messages: [],
});

const VISIBILIYY_TIME_DELAY = 60; // minutes
const CLOSABILITY_TIME_DELAY = 20; // minutes
const CLOSABILITY_INTERVAL = 1000; // milliseconds

// default timout for alert is set to 15 min
export const AlertProvider = ({ children }) => {
    const timerResetRef = useRef(false);
    const { settings } = useContext(SettingsContext);
    const timeoutsRef = useRef([]);
    const [messages, setMessage] = useState([]);

    // return time diff in minutes
    const calculateTimeDiff = (startDate, endDate) => moment.duration(moment(endDate).diff(new Date(startDate))).asMinutes();

    const calculateCloseTimeout = (startDate, endDate) => {
        const timeDiff = calculateTimeDiff(startDate, endDate);
        const timeout = moment.duration(moment(new Date(endDate)).add(timeDiff, "minutes").diff(new Date())).asMilliseconds();

        return timeout <= 0 ? 0 : timeout;
    };

    const canBeClosed = (startDate) => moment(startDate).subtract(CLOSABILITY_TIME_DELAY, "minutes").toDate() > new Date();

    const canBeShown = (startDate, endDate) => {
        const timeDiff = calculateTimeDiff(startDate, endDate);

        return moment(startDate).subtract(VISIBILIYY_TIME_DELAY, "minutes").toDate() < new Date() && moment(endDate).add(timeDiff) > new Date();
    };

    useEffect(() => {
        let interval;

        // checks if a message can be closed in a given time interval
        const checkMessagesClosability = () => {
            const alerts = messages.concat();
            const isEveryAlertUnclosable = alerts.every(({ isClosable }) => !isClosable);

            // clear timeout if there is no more closable messages
            if (isEveryAlertUnclosable) {
                clearInterval(interval);
                timerResetRef.current = true;
            }

            alerts.forEach(({ id, closableUntil, isClosable }) => {
                if (!canBeClosed(closableUntil) && isClosable) {
                    setMessage((currentNotifications) => {
                        return currentNotifications.map((currentNotification) => {
                            if (currentNotification.id === id) {
                                return {
                                    ...currentNotification,
                                    isClosable: false,
                                };
                            }

                            return currentNotification;
                        });
                    });
                }
            });
        };

        // if messages are populated and timer ref wasn't set
        if (messages.length && !timerResetRef.current) {
            interval = setInterval(checkMessagesClosability, CLOSABILITY_INTERVAL);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [messages]);

    const removeQueuedNotification = () => {
        setMessage((allNotifications) => {
            const messageToRemove = _.minBy(allNotifications, (message) => message.id);

            return allNotifications.filter(({ id }) => id !== messageToRemove.id);
        });
    };

    const toggleAlert = useCallback(
        (config = {}) => {
            const { message, type = "warning", group } = config;
            // filter "local" messages with given "messageGroup" and 60 min before startDate
            const groupNotifications =
                settings.globalNotifications && settings.globalNotifications.messages.filter(({ isActive, messageGroup, startDate, endDate }) => isActive && messageGroup === group && canBeShown(startDate, endDate));
            // filter "global" messages - "messageGroup" should be null/undefined and 60 min before startDate
            const globalNotifications = settings.globalNotifications.messages.filter(({ isActive, messageGroup, startDate, endDate }) => isActive && !messageGroup && canBeShown(startDate, endDate));
            const notifications = [...globalNotifications, ...groupNotifications];

            // if no group/local notifications are available, don't display anything
            if (!notifications.length) {
                return;
            }

            // clear particular message automatically based on the "startDate"
            const addMessageToQueue = (id, startDate, endDate) => timeoutsRef.current.push({ id, timeout: setTimeout(removeQueuedNotification, calculateCloseTimeout(startDate, endDate)) });

            setMessage((allNotifications) => {
                // filter only new messages
                const newNotifications = notifications.filter(({ message: newMessage }) => allNotifications.some(({ message: existingMessage }) => newMessage !== existingMessage));
                const notificationsToDisplay = newNotifications.length === 0 ? notifications : newNotifications;

                return [
                    ...notificationsToDisplay.map(({ message: groupMessage, startDate, endDate }, index) => {
                        const id = Date.now();

                        addMessageToQueue(id, startDate, endDate);

                        return {
                            id: `${Date.now()}_${index}`,
                            isToggled: true,
                            message: groupMessage || message,
                            closableUntil: moment(startDate).subtract(CLOSABILITY_TIME_DELAY, "minutes").toDate(),
                            isClosable: canBeClosed(startDate), // disable "close" button 15 min before "startDate"
                            type,
                        };
                    }),
                ];
            });
        },
        [settings]
    );

    // close particular message and remove it from timeout queue
    const closeAlert = useCallback((notificationId) => {
        const removeMessageFromQueue = () => {
            timeoutsRef.current.forEach(({ id, timeout }) => {
                if (notificationId === id) {
                    clearTimeout(timeout);
                }
            });

            timeoutsRef.current.splice(
                timeoutsRef.current.findIndex(({ id }) => id === notificationId),
                1
            );
        };

        setMessage((allNotifications) => allNotifications.filter(({ id }) => id !== notificationId));

        removeMessageFromQueue();
    }, []);

    return <AlertContext.Provider value={{ messages, toggleAlert, closeAlert }}>{children}</AlertContext.Provider>;
};
